import type { WishlistItemWithWishlistId } from './wishlist.types';

/**
 * Checks if a wishlist item matches the given identifiers
 *
 * @param item - The wishlist item to check
 * @param productGroupIdentifier - Optional product group identifier
 * @param sku - Optional SKU
 * @returns true if the item matches the identifiers
 */
export function itemMatchesIdentifiers(
  item: WishlistItemWithWishlistId,
  productGroupIdentifier?: string,
  sku?: string
): boolean {
  // If we have both productGroupIdentifier and sku (specific product)
  if (productGroupIdentifier && sku) {
    return (
      item.product_group_identifier === productGroupIdentifier &&
      item.sku === sku
    );
  }

  // If we only have productGroupIdentifier with sku as null/undefined (product group)
  if (productGroupIdentifier && !sku) {
    return (
      item.product_group_identifier === productGroupIdentifier &&
      (item.sku === null || item.sku === undefined)
    );
  }

  // If we only have sku (fallback case)
  if (sku && !productGroupIdentifier) {
    return item.sku === sku;
  }

  return false;
}

/**
 * Find a matching item in the wishlist items array
 * Can optionally filter by wishlistId
 *
 * @param items - Array of wishlist items to search
 * @param productGroupIdentifier - Optional product group identifier
 * @param sku - Optional SKU
 * @param wishlistId - Optional wishlist ID to filter by
 * @returns The matching item or undefined
 */
export function findMatchingItem(
  items: WishlistItemWithWishlistId[],
  productGroupIdentifier?: string,
  sku?: string,
  wishlistId?: string
): WishlistItemWithWishlistId | undefined {
  return items.find((item) => {
    // If wishlistId is specified, filter by it first
    if (wishlistId && item.wishlistId !== wishlistId) {
      return false;
    }
    return itemMatchesIdentifiers(item, productGroupIdentifier, sku);
  });
}

/**
 * Check if any item matches the given identifiers
 * Can optionally filter by wishlistId
 *
 * @param items - Array of wishlist items to search
 * @param productGroupIdentifier - Optional product group identifier
 * @param sku - Optional SKU
 * @param wishlistId - Optional wishlist ID to filter by
 * @returns true if a matching item exists
 */
export function hasMatchingItem(
  items: WishlistItemWithWishlistId[],
  productGroupIdentifier?: string,
  sku?: string,
  wishlistId?: string
): boolean {
  return items.some((item) => {
    // If wishlistId is specified, filter by it first
    if (wishlistId && item.wishlistId !== wishlistId) {
      return false;
    }
    return itemMatchesIdentifiers(item, productGroupIdentifier, sku);
  });
}
